import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
export const handleExportButton = async (analysisResultId) => {
  let reportData = JSON.parse(analysisResultId?.reportData);
  let fleet = [reportData?.fleet];
  let fleetMix = [...reportData?.fleetMix];
  let maintenance = [reportData?.maintainanceCost];
  let cycling = [reportData?.cyclingComparision];
  let fuelCost = [reportData?.fuelCost];
  const maintenanceStartCol = 4;
  const hrStartCol = 4;
  const FuelStartCol = 1;
  const FleetMixCol = 1;
  const combinedData = {
    Fleet: fleet.map((item) => ({
      "Current Fleet Size": item?.currentFleetSize,
      "Avg. Vehicle Model Year": item?.avgVehicleYear,
      "Avg. New Vehicle Price": item?.avgNewVehiclePrice,
      "Avg. Current Mileage": Math.round(item?.currentMileage),
      "Current Cycles": item?.currentCycle,
      "Avg. Annual Miles": Math.round(item?.annualMiles),
    })),
    Maintenance: maintenance.map((item) => ({
      "Total Annualized Spend": item?.totalAnnualized?.toFixed(2),
      "Per Vehicle Spend": item?.vehicleSpend?.toFixed(2),
      "Vehicle Spend/Month": item?.vehicleSpendMonth?.toFixed(2),
      "Avg. Cost Per Mile Per Vehicle Spend": item?.costPerMile?.toFixed(2),
    })),
    CyclingComparision: cycling.map((item) => ({
      "Avg. Year 1 Cost Per Miles": item?.avgYear1CostPerMiles?.toFixed(2),
      "Avg. Year 1 MPG": item?.avgYear1Mpg?.toFixed(2),
      "Fuel Spend Year 1": item?.fuelSpendY1?.toFixed(2),
      "Monthly Lease Cost": item?.monthlyLeaseCost,
      "Monthly Maintanance Year1": item?.monthlyMaintananceYear1?.toFixed(2),
      ProposedCycle: item?.proposedCycle,
    })),
    FuelCost: fuelCost.map((item) => ({
      "Annual Spend Vehicle": item?.annualSpendVehicle?.toFixed(2),
      "Avg. Annual Miles": Math.round(item?.avgAnnualMiles),
      "Avg. Mpg": item?.avgMpg?.toFixed(2),
      "Cost Per Gallon": item?.costPerGallon?.toFixed(2),
      "Monthly Spend Vehicle": item?.monthlySpendVehicle?.toFixed(2),
      "Number Of Vehicle": item?.numberOfVehicle,
      "Total Fuel Spend": item?.totalFuelSpend?.toFixed(2),
    })),
    FleetMix: fleetMix.map((item) => ({
      "Fiscal Year": item?.fiscalYear,
      "Fleet Size": item?.fleetSize,
      "Annual Needs": item?.annualNeeds,
      "Purchased/Financed": item?.purchasedFinancial,
      "Cycling Payment": item?.cyclingPayment,
      "Cash Outlay": item?.cashOutlay,
      "Current Payment": item?.currentPayment,
      Maintenance: item?.maintenance,
      Fuel: item?.fuel,
      "Fleet Spend": item?.fleetSpeed,
      "Vehicle Equity": item?.vehicleEquity,
      Savings: item?.savings,
    })),
  };
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("PowerBi-Report");

  // Define styles
  const headerStyle = {
    font: { bold: true },
    border: {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    },
    fill: {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FDFD97" }, // Yellow fill color
    },
  };

  const sectionHeaderStyle = {
    font: { bold: true, size: 14 },
    border: {
      bottom: { style: "thin" },
    },
    fill: {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "8DB4E2" }, // Light purple fill color
    },
  };
  const cellStyle = {
    border: {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
      wrapText: true,
    },
  };
  const boldCellStyle = {
    font: { bold: true },
    border: {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    },
  };
  const data = combinedData;
  let currentRow = 1;

  // Helper function to add a transposed section
  const addTransposedSection = (sectionName, items) => {
    if (!items || items.length === 0) return;

    // Add section heading and merge cells
    worksheet.mergeCells(currentRow, 1, currentRow, 2);
    const sectionCell = worksheet.getCell(currentRow, 1);
    sectionCell.value = sectionName;
    sectionCell.style = sectionHeaderStyle;
    currentRow++;

    // Determine headers based on data keys
    const headers = Object.keys(items[0]);

    // Add transposed headers
    headers.forEach((header, index) => {
      const cell = worksheet.getCell(currentRow + index, 1);
      cell.value = header;
      cell.style = headerStyle;
    });

    // Add transposed data rows
    items.forEach((item, colIndex) => {
      headers.forEach((header, rowIndex) => {
        const cell = worksheet.getCell(currentRow + rowIndex, colIndex + 2);

        if (typeof item[header] === "number") {
          cell.value = item[header]; // This will set the value as a number
        } else {
          cell.value = isNaN(Number(item[header])) ? item[header] : Number(item[header]); // For non-numeric values
        }
        cell.style = cellStyle;
      });
    });

    // Move currentRow to the next empty row after this section
    currentRow += headers.length + 2;
  };

  // Helper function to add a standard table section
  const addTableSection = (sectionName, items) => {
    if (!items || items.length === 0) return;

    const headers = Object.keys(items[0]);

    // Add section heading and merge cells across all header columns
    worksheet.mergeCells(currentRow, 1, currentRow, headers.length);
    const sectionCell = worksheet.getCell(currentRow, 1);
    sectionCell.value = sectionName;
    sectionCell.style = sectionHeaderStyle;
    currentRow++;

    // Add headers
    headers.forEach((header, index) => {
      const cell = worksheet.getCell(currentRow, index + 1);
      cell.value = header;
      cell.style = headerStyle;
    });
    currentRow++;

    // Add data rows
    items.forEach((item) => {
      headers.forEach((header, colIndex) => {
        const cell = worksheet.getCell(currentRow, colIndex + 1);
        cell.value = item[header];
        cell.style = cellStyle;
        if (header === "Fiscal Year") {
          cell.style = boldCellStyle;
        }
      });
      currentRow++;
    });

    // Handle Total Savings for Fleet Mix
    if (sectionName === "Fleet Mix") {
      const totalSavings = items.reduce(
        (sum, item) => sum + (item.Savings || 0),
        0
      );
      const savingsCol = headers.indexOf("Savings") + 1;
      const labelCol = headers.indexOf("Vehicle Equity") + 1;

      const totalLabelCell = worksheet.getCell(currentRow, labelCol > 0 ? labelCol : headers.length - 1);
      totalLabelCell.value = "Total Savings";
      totalLabelCell.style = headerStyle;

      const totalCell = worksheet.getCell(currentRow, savingsCol > 0 ? savingsCol : headers.length);
      totalCell.value = totalSavings;
      totalCell.style = headerStyle;
      currentRow++;
    }

    // Add a blank row between sections
    currentRow += 2;
  };

  if (data.Fleet) addTransposedSection("Fleet", data.Fleet);
  if (data.Maintenance) addTransposedSection("Maintenance Cost", data.Maintenance);
  if (data.FuelCost) addTransposedSection("Fuel Cost", data.FuelCost);
  if (data.CyclingComparision) addTransposedSection("Cycling Comparision", data.CyclingComparision);
  if (data.FleetMix) addTableSection("Fleet Mix", data.FleetMix);

  // For any other dynamic sections
  for (const [section, items] of Object.entries(data)) {
    if (
      section === "Fleet" ||
      section === "Maintenance" ||
      section === "FuelCost" ||
      section === "CyclingComparision" ||
      section === "FleetMix"
    )
      continue;
      
    addTableSection(section, items);
  }

  worksheet.columns.forEach((column) => {
    let maxLength = 0;
    const columnCells = column.values.filter(
      (val) => val !== undefined && val !== null
    );
    columnCells.forEach((cell) => {
      const columnLength = cell.toString().length;
      if (columnLength > maxLength) {
        maxLength = columnLength;
      }
    });
    column.width = maxLength < 10 ? 10 : maxLength;
  });
  // Save the workbook
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `${analysisResultId?.reportName}.xlsx`);
};
